<template>
    <div
        class="position-relative"
        itemtype="https://schema.org/ItemList"
        itemscope
    >

        <!-- Scroll Detection Element -->
        <div
            v-show="hasPrevLink"
            ref="listStartTracker"
            class="list-start-tracker"
        ></div>

        <v-row
            :dense="isMobile"
        >
            <v-col
                v-if="productsLoading"
                class="d-flex flex-column justify-center align-center my-10 text-overline"
            >
                <v-progress-circular
                    color="accent"
                    size="48"
                    width="6"
                    indeterminate
                ></v-progress-circular>
            </v-col>
            <template
                v-else-if="hasProducts"
            >
                <v-col
                    v-for="product in products"
                    :key="`prod-col-column-${product.uid}`"
                    cols="12"
                    sm="6"
                    md="4"
                    class="d-flex flex-grow-1 flex-column align-stretch justify-stretch"
                    itemprop="itemListElement"
                    itemtype="https://schema.org/ListItem"
                    itemscope
                >
                    <ProductsListItem
                        :key="`prod-col-item-${product.uid}`"
                        :item="product"
                        :select-first="selectFirstItem"
                        :page-key="pageKey"
                        :brand-title="brandTitle"
                    ></ProductsListItem>
                </v-col>
            </template>
            <v-col
                v-else
            >
                <h3
                    class="mt-4 text-center"
                >
                    No products found
                </h3>
            </v-col>
        </v-row>

        <!-- Scroll Detection Element -->
        <div
            v-show="hasNextLink && !productsLoading"
            ref="listEndTracker"
            class="list-end-tracker"
        ></div>

        <div
            v-if="hasNextLink && products && products.length && !productsLoading"
            class="d-flex justify-center mt-4"
        >
            <v-progress-circular
                color="accent"
                width="6"
                size="48"
                indeterminate
            ></v-progress-circular>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { useIsMobile } from '~/composables';
    import type { Product } from '~/types';
    import ProductsListItem from '~/components/products/products-list/ProductsListItem/ProductsListItem.vue';

    const props = defineProps<{
        products: Nilish<Product[]>;
        productsLoading: boolean;
        productsPaging: boolean;
        pageKey: string;
        hasNextLink: boolean,
        hasPrevLink: boolean,
        filtersAppliedCount: number;
    }>();

    const emit = defineEmits<{
        'on-get-prev': [value: void];
        'on-get-next': [value: void];
    }>();

    const $config = useRuntimeConfig();
    const brandTitle = $config.public.brand.title;
    const { isMobile } = useIsMobile();
    const listStartTracker = ref<Element>();
    const listEndTracker = ref<Element>();
    const selectFirstItem = computed<boolean>(() => !!props.filtersAppliedCount);
    const hasProducts = computed(() => Boolean(props.products?.length));

    let observer: IntersectionObserver;

    // Scroll Intersect
    function onIntersect(entries: IntersectionObserverEntry[]): void {
        if (props.productsPaging) {
            return;
        }

        entries.forEach((item) => {
            const name = item.target.className;

            if (item.intersectionRatio > 0) {
                // TODO: Reserved for when we implement prev cleanup
                // if (name === 'list-start-tracker') {
                //     emit('on-get-prev');
                // }

                if (name === 'list-end-tracker') {
                    emit('on-get-next');
                }
            }
        });
    }

    onMounted(() => {
        if (listStartTracker.value && listEndTracker.value) {
            observer = new IntersectionObserver(onIntersect);
            observer.observe(listStartTracker.value);
            observer.observe(listEndTracker.value);
        }
    });

    onBeforeUnmount((): void => {
        if (listStartTracker.value && listEndTracker.value && observer) {
            observer.unobserve(listStartTracker.value);
            observer.unobserve(listEndTracker.value);
        }
    });
</script>

<style lang="scss" scoped>
.list-start-tracker,
.list-end-tracker {
    position: absolute;
    z-index: -1;
    width: 100%;
    height: 20rem;
}

.list-start-tracker {
    top: 0;
}

.list-end-tracker {
    bottom: 0;
}
</style>
